/**
 * Three screens for the child safety rules (worker/visibility.js, worker/vetting.js).
 *
 *   The player's card in Settings: an adult's opt out, the parent link for a
 *   minor (a parent's email, confirmed from that inbox), a parent's named
 *   grants (with the parent PIN), who has looked, with a way to report them.
 *   Verification (the scout desk view): every requirement a coach, scout or
 *   agent must pass, its state, and the steps that are theirs to do.
 *   The scout desk: search and a read only record, for a vetted scout or agent.
 *
 * The server decides everything. These screens only say what it decided.
 */
let ctx = null;
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const post = (path, body, method = 'POST') => ctx.api(path, { method, body: JSON.stringify(body || {}) });

export function mount(c) { ctx = c; }

/* ------------------------------------------------------------- settings */

export async function renderVisibility() {
  const root = document.getElementById('visibilityRoot'); if (!root) return;
  let v;
  try { v = await ctx.api('/api/me/visibility'); }
  catch (e) {
    root.innerHTML = `<div class="card"><h3>Scouts and agents</h3><p class="sub">${esc(e.message)}</p><p class="sub">Without an account no scout can find this record at all.</p></div>${proCard()}`;
    bindPro(root);
    return;
  }
  const grants = (v.grants || []).map((g) => `
    <li class="vis-grant"><b>${esc(g.label || (g.to.kind === 'org' ? 'A program' : 'A scout'))}</b>
      <small>${g.audience === 'coach' ? 'team share (verified coaches and linked parents)' : g.to.kind === 'org' ? 'program' : 'scout'} &middot; ${(g.scopes || []).join(', ')}${g.until ? ` &middot; until ${when(g.until)}` : ''}</small>
      <button class="btn" data-revoke="${esc(g.id)}">Revoke</button></li>`).join('');
  const views = (v.views || []).slice(0, 8).map((x) => `<li class="vis-grant"><small>${when(x.at)} &middot; ${esc(x.name || 'a scout')} (${esc(x.role || 'scout')}) read ${(x.scopes || []).join(', ')}</small>
    ${x.by ? `<button class="btn" data-report-view="${esc(x.by)}">Report</button>` : ''}</li>`).join('');
  const kid = v.age === 'kid';
  root.innerHTML = `
    ${v.age === 'unknown' ? `
    <div class="card" style="margin-bottom:14px">
      <h3>Nobody can see this player yet</h3>
      <p class="sub">No age is set, so this record is hidden from every coach, team, scout and agent, and nobody can reach this player in Compete. Choose the age range in Settings below. Under 18, a parent does it with the parent PIN.</p>
    </div>` : ''}
    ${v.minor && v.age !== 'unknown' ? guardianCard(v) : ''}
    <div class="card" style="margin-bottom:14px" id="visCard">
      <h3>Scouts and agents</h3>
      <p class="sub">${esc(v.rule)}</p>
      ${v.minor ? `
        <p class="sub">${v.age === 'unknown' ? 'Set the age first.' : kid && !v.consent ? 'Under 13, a parent confirms their email in the card above first. Then a parent can name a scout here with the parent PIN.'
          : v.pinSet ? 'A parent names a verified scout or a program here with the parent PIN. Shooting and the record only, unless the grant says more.' : 'Set the parent PIN in this Settings page first. A grant for a minor is the parent’s to make.'}</p>
        <ul class="vis-list">${grants || '<li><small>No grants. This record is invisible to every scout and agent.</small></li>'}</ul>
        ${v.pinSet && v.age !== 'unknown' && !(kid && !v.consent) ? `
        <div class="vis-form">
          <input id="visEmail" class="gmin" placeholder="Verified scout's account email" autocomplete="off">
          <span class="sub">or</span>
          <input id="visOrg" class="gmin" placeholder="Program id (from the program's owner)" autocomplete="off">
          <input id="visLabel" class="gmin" placeholder="Name it, e.g. Coach Rivera's AAU program" maxlength="80">
          <label class="sub"><input type="checkbox" id="visHealth"> also body and health</label>
          <label class="sub"><input type="checkbox" id="visJournal"> also journal</label>
          <input id="visDays" class="gmin" type="number" min="1" max="365" placeholder="Days (blank: until revoked)" style="max-width:190px">
          <input id="visPin" class="gmin" type="password" placeholder="Parent PIN" maxlength="8" style="max-width:140px">
          <button class="btn primary" id="visGrant">Grant</button>
        </div>` : ''}
      ` : `
        <div class="srow">
          <div class="lab"><b>Visible to verified scouts and agents</b><small>Your record, sessions and ratings. Never your journal, body or food. Turn this off and you are out of their search entirely.</small></div>
          <div class="sw ${v.scoutOptOut ? '' : 'on'}" id="swScouts" role="switch" aria-checked="${v.scoutOptOut ? 'false' : 'true'}"></div>
        </div>`}
      <h4 style="margin:14px 0 4px">Who looked</h4>
      <ul class="vis-list">${views || '<li><small>Nobody yet.</small></li>'}</ul>
      <p class="sub">Something about a scout or agent feel wrong? Report them. A person reads every report, and a scout or agent reported for a safety reason (the first four) loses access while it is reviewed.</p>
    </div>
    ${proCard()}`;
  root.querySelector('#swScouts')?.addEventListener('click', async (e) => {
    const on = !e.currentTarget.classList.contains('on');
    try { await ctx.api('/api/me/visibility', { method: 'PATCH', body: JSON.stringify({ scoutOptOut: !on }) }); renderVisibility(); }
    catch (err) { ctx.toast(err.message, true); }
  });
  root.querySelector('#visGrant')?.addEventListener('click', async () => {
    const scopes = ['performance']; if (root.querySelector('#visHealth').checked) scopes.push('health'); if (root.querySelector('#visJournal').checked) scopes.push('journal');
    const days = Number(root.querySelector('#visDays').value) || undefined;
    try {
      await post('/api/me/grants', { scoutEmail: root.querySelector('#visEmail').value.trim() || undefined, orgId: root.querySelector('#visOrg').value.trim() || undefined, label: root.querySelector('#visLabel').value.trim(), scopes, days, pin: root.querySelector('#visPin').value });
      ctx.toast('Granted. You can revoke it here any time.'); renderVisibility();
    } catch (err) { ctx.toast(err.message, true); }
  });
  root.querySelectorAll('[data-revoke]').forEach((b) => b.addEventListener('click', async () => {
    const pin = v.minor && v.pinSet ? prompt('Parent PIN to revoke this grant') : '';
    if (v.minor && v.pinSet && !pin) return;
    try { await post(`/api/me/grants/${b.dataset.revoke}`, { pin }, 'DELETE'); ctx.toast('Revoked.'); renderVisibility(); }
    catch (err) { ctx.toast(err.message, true); }
  }));
  root.querySelectorAll('[data-report-view]').forEach((b) => b.addEventListener('click', () => reportAccount(b.dataset.reportView)));
  bindGuardian(root, v);
  bindPro(root);
}

/** The parent link, on the child's own Settings. The PIN starts it; the parent's inbox confirms it. */
function guardianCard(v) {
  const links = (v.guardians || []).map((g) => `
    <li class="vis-grant"><b>${esc(g.email)}</b>
      <small>${g.state === 'confirmed' ? `<span class="pill good">Confirmed</span> ${when(g.confirmedAt)}` : `<span class="pill cool">Waiting</span> sent ${when(g.requestedAt)}, the parent opens the link in that inbox`}</small>
      <button class="btn" data-unlink="${esc(g.email)}">Remove</button></li>`).join('');
  const kid = v.age === 'kid';
  return `
    <div class="card" style="margin-bottom:14px">
      <h3>Parent or guardian</h3>
      <p class="sub">${kid
        ? (v.consent ? 'A parent has confirmed. Their consent is on record, and they see this player in any family or team they are both in.'
          : '<b>Under 13, nobody outside this account sees or reaches this player until a parent confirms their email here.</b> No team, no coach, no scout, no challenge.')
        : 'Link a parent or guardian. Once they confirm from their own email, they see this player in any family or team they are both in, and nobody else’s parents do.'}</p>
      <ul class="vis-list">${links || '<li><small>No parent linked yet.</small></li>'}</ul>
      ${v.pinSet ? `
      <div class="vis-form">
        <input id="gdEmail" class="gmin" type="email" placeholder="Parent or guardian email" autocomplete="off" maxlength="200">
        <input id="gdPin" class="gmin" type="password" placeholder="Parent PIN" maxlength="8" style="max-width:140px">
        <button class="btn primary" id="gdSend">Send the confirmation</button>
      </div>
      <p class="sub">The parent gets a link that works once, for seven days. Nothing is linked until they open it. At most two per player.</p>`
      : '<p class="sub">Set the parent PIN in this Settings page first. Linking a parent is the parent’s step.</p>'}
    </div>`;
}

function bindGuardian(root, v) {
  root.querySelector('#gdSend')?.addEventListener('click', async () => {
    const email = root.querySelector('#gdEmail').value.trim();
    if (!email) { ctx.toast('Enter the parent’s email first.', true); return; }
    try {
      const r = await post('/api/me/grants/guardian', { email, pin: root.querySelector('#gdPin').value });
      ctx.toast(r.already ? 'That parent is already confirmed.' : `Sent to ${email}. Nothing is linked until they open it.`);
      renderVisibility();
    } catch (err) { ctx.toast(err.message, true); }
  });
  root.querySelectorAll('[data-unlink]').forEach((b) => b.addEventListener('click', async () => {
    const pin = v.pinSet ? prompt('Parent PIN to remove this parent link') : '';
    if (v.pinSet && !pin) return;
    try { await post('/api/me/grants/guardian', { email: b.dataset.unlink, pin }, 'DELETE'); ctx.toast('Removed.'); renderVisibility(); }
    catch (err) { ctx.toast(err.message, true); }
  }));
}

/** The way in for a coach, a scout or an agent. */
function proCard() {
  return `
    <div class="card" style="margin-bottom:14px">
      <h3>Coaches, scouts and agents</h3>
      <p class="sub">Every coach, scout and agent passes a verification before they see any player under 18 on a team roster, in a group's shared view, on a board or through the scout desk, and before they can invite anyone into a team: identity, a background check with the sex offender registry, abuse prevention training, their credential, two references, our code of conduct and their work email. A person checks each one by hand, and approval can take a while.</p>
      <div class="vis-form"><button class="btn" id="proOpen">Open verification</button></div>
    </div>`;
}
function bindPro(root) { root.querySelector('#proOpen')?.addEventListener('click', () => ctx.go('scouts')); }

const REPORT_REASONS = [
  ['safety', 'Something they did made me feel unsafe'],
  ['private', 'They asked to talk in private or off Hooper Buddy'],
  ['gifts', 'They offered gifts, money or rides'],
  ['harassment', 'They are bothering me'],
  ['other', 'Something else'],
];
async function reportAccount(accountId) {
  const pick = prompt(`What happened? Type the number.\n${REPORT_REASONS.map(([, l], i) => `${i + 1}. ${l}`).join('\n')}`);
  const reason = REPORT_REASONS[Number(pick) - 1]?.[0];
  if (!reason) return;
  const details = prompt('A few words for the person who reads it (optional). Only Hooper Buddy sees this.') || '';
  try { await post('/api/social/report', { accountId, reason, details }); ctx.toast('Report sent. A person reads every report.'); }
  catch (err) { ctx.toast(err.message, true); }
}

/* ------------------------------------------------ verification and desk */

const STATE_PILL = {
  verified: '<span class="pill good">Verified</span>',
  missing: '<span class="pill cool">Waiting</span>',
  expired: '<span class="pill hot">Ran out</span>',
  failed: '<span class="pill hot" style="color:var(--bad);border-color:var(--bad)">Did not pass</span>',
};
const ACCESS_WORD = { approved: 'Approved', in_review: 'In review', lapsed: 'Paused: a check ran out', changed: 'Paused: waiting on a fresh approval', suspended: 'Suspended while it is reviewed', rejected: 'Not approved', withdrawn: 'Withdrawn', none: 'Not started' };

function checksHtml(vt) {
  return `<ul class="vis-list">${(vt.checks || []).map((c) => `
    <li class="vis-grant" style="align-items:flex-start;flex-wrap:wrap">
      <b style="flex:1 1 150px">${esc(c.label)}<br><small style="font-weight:600">${c.who === 'you' ? 'Your step' : 'Checked by Hooper Buddy'}</small></b>
      <small style="flex:3 1 200px">${esc(c.what || '')}${c.why ? ` ${esc(c.why)}` : ''}${c.expiresAt && c.state === 'verified' ? ` Good until ${when(c.expiresAt)}.` : ''}</small>
      <span style="margin-left:auto">${STATE_PILL[c.state] || ''}</span></li>`).join('')}</ul>`;
}

function applicationForm(vt) {
  const a = vt.application || {};
  const refs = a.references || [];
  return `
    <div class="card" style="margin-bottom:14px">
      <h3>Your application</h3>
      <p class="sub">Tell us who you are. Nothing here is taken as proof: every item is checked by a person, through sources we find ourselves.</p>
      <div class="vis-form">
        <select id="vtRole" class="gmin">${['coach', 'scout', 'agent'].map((r) => `<option value="${r}"${a.role === r ? ' selected' : ''}>${r[0].toUpperCase() + r.slice(1)}</option>`).join('')}</select>
        <input id="vtName" class="gmin" placeholder="Full legal name, as on your ID" maxlength="80" value="${esc(a.legalName || '')}">
        <input id="vtOrg" class="gmin" placeholder="School, club, program, college, service or agency" maxlength="120" value="${esc(a.organization || '')}">
        <input id="vtPos" class="gmin" placeholder="Your position there" maxlength="80" value="${esc(a.position || '')}">
        <input id="vtEmail" class="gmin" type="email" placeholder="Work email" maxlength="200" value="${esc(a.workEmail || '')}">
      </div>
      <div class="vis-form" id="vtAgent" style="${a.role === 'agent' ? '' : 'display:none'}">
        <input id="vtStates" class="gmin" placeholder="States you work in, e.g. TX, OK" maxlength="120" value="${esc((a.states || []).join(', '))}">
        <input id="vtAssoc" class="gmin" placeholder="Players association certification, if any" maxlength="120" value="${esc(a.association || '')}">
      </div>
      <h4 style="margin:14px 0 4px">Two professional references</h4>
      ${[0, 1].map((i) => `<div class="vis-form">
        <input id="vtR${i}n" class="gmin" placeholder="Name" maxlength="80" value="${esc(refs[i]?.name || '')}">
        <input id="vtR${i}r" class="gmin" placeholder="How they know your work" maxlength="80" value="${esc(refs[i]?.relation || '')}">
        <input id="vtR${i}c" class="gmin" placeholder="Their email or phone" maxlength="120" value="${esc(refs[i]?.contact || '')}">
      </div>`).join('')}
      <div class="vis-form"><input id="vtNote" class="gmin" placeholder="Anything else we should know (optional)" maxlength="500"><button class="btn primary" id="vtSend">${vt.application ? 'Update application' : 'Send application'}</button></div>
    </div>`;
}

/** Coming soon: coaches, scouts and agents join a waitlist until the legal review is done. */
function renderWaitlist(root) {
  root.innerHTML = `
    <div class="vhead"><div><h1>Coaches, scouts and agents</h1><p><span class="pill cool">Coming soon</span></p></div></div>
    <div class="card" style="margin-bottom:14px">
      <h3>Join the waitlist</h3>
      <p class="sub">Reviewing players' data is not open yet. We are finishing a legal review first, because many of our players are kids. Leave your details and we will tell you when it opens. Every coach, scout and agent will still be checked before they can see a player under 18.</p>
      <form id="proWait" class="vis-form" style="display:grid;gap:8px;max-width:420px">
        <input name="name" required maxlength="120" placeholder="Your name" autocomplete="name">
        <input name="email" type="email" required maxlength="200" placeholder="Work email" autocomplete="email">
        <select name="role" required>
          <option value="coach">Coach</option><option value="scout">Scout</option><option value="agent">Agent</option>
          <option value="trainer">Trainer</option><option value="recruiter">Recruiter</option>
        </select>
        <input name="org" maxlength="120" placeholder="School, club, program or agency">
        <button class="btn primary" type="submit">Join the waitlist</button>
      </form>
      <p class="sub" id="proWaitMsg" role="status"></p>
    </div>`;
  const form = root.querySelector('#proWait');
  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const d = Object.fromEntries(new FormData(form));
    const msg = root.querySelector('#proWaitMsg');
    try {
      await ctx.api('/api/waitlist', { method: 'POST', body: JSON.stringify(d) });
      form.hidden = true; msg.textContent = "You're on the list. We'll email you when it opens.";
    } catch (e) { msg.textContent = e.message || 'That did not go through. Try again.'; }
  });
}

export async function render() {
  const root = document.getElementById('v-scouts'); if (!root) return;
  let me;
  try { me = await ctx.api('/api/scouts/me'); }
  catch (e) { root.innerHTML = `<div class="vhead"><div><h1>Verification</h1><p>Sign in first.</p></div></div>`; return; }
  if (me.prosOpen === false) return renderWaitlist(root);
  if (!me.signedIn) { root.innerHTML = `<div class="vhead"><div><h1>Verification</h1><p>Sign in first, from Settings.</p></div></div>`; return; }
  const vt = me.vetting || { status: 'none', checks: [], access: { state: 'none' } };
  const state = vt.access?.state || 'none';
  const locked = vt.application && !['none', 'withdrawn'].includes(vt.status) && (vt.checks || []).some((c) => c.who === 'admin' && c.state !== 'missing');
  const canApply = !vt.suspended && !vt.rejected && (!vt.application || vt.status === 'withdrawn' || (vt.status === 'submitted' && !locked));
  const head = `
    <div class="vhead"><div><h1>${me.scout ? 'Scout desk' : 'Verification'}</h1>
      <p>${esc(vt.manual || 'Approval is manual. A person checks every item, and it can take days or weeks. Until then this account sees no player under 18.')}</p></div></div>
    <div class="card" style="margin-bottom:14px">
      <h3>Status</h3>
      <p><span class="pill ${vt.access?.ok ? 'good' : 'cool'}">${esc(ACCESS_WORD[state] || state)}</span>
        ${vt.application ? ` as a ${esc(vt.application.role)}${vt.application.organization ? `, ${esc(vt.application.organization)}` : ''}` : ''}
        ${vt.access?.ok && vt.access.until ? ` &middot; until ${when(vt.access.until)}` : ''}</p>
      ${vt.access?.reason && !vt.access.ok && state !== 'none' ? `<p class="sub">${esc(vt.access.reason)}</p>` : ''}
      ${vt.rejected ? '<p class="sub">If you think this is wrong, write to info@hooperbuddy.com.</p>' : ''}
      ${vt.application ? '' : '<p class="sub">Coaches, scouts and agents: nothing on Hooper Buddy shows you a player under 18 until every check below is verified and a person approves it. Adults who have not opted out are open to verified scouts; minors only when their parent names you.</p>'}
      ${(vt.checks || []).length ? checksHtml(vt) : ''}
    </div>`;
  const steps = vt.application && !['withdrawn', 'rejected'].includes(vt.status) && !vt.suspended ? `
    <div class="card" style="margin-bottom:14px">
      <h3>Your two steps</h3>
      <h4 style="margin:6px 0 6px">Code of conduct, version ${esc(vt.conduct?.version)}</h4>
      <ol style="margin:0 0 10px 18px;line-height:1.6">${(vt.conduct?.rules || []).map((r) => `<li>${esc(r)}</li>`).join('')}</ol>
      <div class="vis-form">${vt.conduct?.accepted ? '<span class="pill good">Accepted</span>' : '<button class="btn primary" id="vtConduct">I accept this code of conduct</button>'}</div>
      <h4 style="margin:14px 0 6px">Work email</h4>
      <div class="vis-form">${vt.email?.confirmed ? `<span class="pill good">Confirmed</span> <small>${esc(vt.email.address)}</small>` : `<button class="btn" id="vtMail">Email a confirmation link to ${esc(vt.email?.address || 'your work email')}</button>`}</div>
      <div class="vis-form"><button class="btn danger" id="vtWithdraw">Withdraw my application</button></div>
    </div>` : '';
  root.innerHTML = head + (canApply ? applicationForm(vt) : '') + steps + (me.scout ? deskHtml(me) : '');
  bindVerification(root, vt);
  if (me.scout) bindDesk(root);
}

function bindVerification(root, vt) {
  const $ = (id) => root.querySelector(`#${id}`);
  // .vis-form sets display, which beats the hidden attribute, so the style is set directly.
  $('vtRole')?.addEventListener('change', (e) => { $('vtAgent').style.display = e.target.value === 'agent' ? '' : 'none'; });
  $('vtSend')?.addEventListener('click', async () => {
    const references = [0, 1].map((i) => ({ name: $(`vtR${i}n`).value.trim(), relation: $(`vtR${i}r`).value.trim(), contact: $(`vtR${i}c`).value.trim() }));
    try {
      await post('/api/scouts/vetting', { role: $('vtRole').value, legalName: $('vtName').value.trim(), organization: $('vtOrg').value.trim(), position: $('vtPos').value.trim(),
        workEmail: $('vtEmail').value.trim(), states: $('vtStates').value, association: $('vtAssoc').value.trim(), references, note: $('vtNote').value.trim() });
      ctx.toast('Application saved. A person reviews it by hand.'); render();
    } catch (err) { ctx.toast(err.message, true); }
  });
  $('vtConduct')?.addEventListener('click', async () => {
    try { await post('/api/scouts/vetting/conduct', { accept: true, version: vt.conduct.version }); ctx.toast('Accepted.'); render(); }
    catch (err) { ctx.toast(err.message, true); }
  });
  $('vtMail')?.addEventListener('click', async () => {
    try { const r = await post('/api/scouts/vetting/email'); ctx.toast(r.confirmed ? 'Already confirmed.' : 'Sent. Open the link from that inbox.'); }
    catch (err) { ctx.toast(err.message, true); }
  });
  $('vtWithdraw')?.addEventListener('click', async () => {
    const ok = await ctx.ui.confirm({ title: 'Withdraw your application?', body: 'Any access ends at once. You can apply again later, and every check starts over.', confirmText: 'Withdraw', danger: true });
    if (!ok) return;
    try { await post('/api/scouts/vetting/withdraw'); ctx.toast('Withdrawn.'); render(); }
    catch (err) { ctx.toast(err.message, true); }
  });
}

function deskHtml(me) {
  return `
    <div class="card"><h3>Search</h3><p class="sub">${esc(me.scout.role)}${me.scout.org ? ` &middot; ${esc(me.scout.org)}` : ''} &middot; read only. Every look is written to the player's own log. Adults who have not opted out, and minors only when their parent named you.</p>
      <div class="vis-form"><input id="scQ" class="gmin" placeholder="Search players by name" autocomplete="off"><button class="btn primary" id="scGo">Search</button></div>
      <div id="scResults"></div></div>
    <div id="scRecord"></div>`;
}

function bindDesk(root) {
  const search = async () => {
    const out = root.querySelector('#scResults'); out.innerHTML = '<p class="sub">Searching&hellip;</p>';
    try {
      const r = await ctx.api(`/api/scouts/search?q=${encodeURIComponent(root.querySelector('#scQ').value.trim())}`);
      out.innerHTML = r.players.length ? `<ul class="vis-list">${r.players.map((p) => `<li class="vis-grant"><b>${esc(p.name)}</b><small>${p.ageBand === 'adult' ? 'adult, open' : 'minor, named to you by a parent'} &middot; ${(p.scopes || []).join(', ')}</small><button class="btn" data-open="${esc(p.id)}">Open</button></li>`).join('')}</ul>${r.partial ? '<p class="sub">The search stopped early: too many accounts to scan in one go. Narrow the name.</p>' : ''}` : '<p class="sub">Nobody visible to you by that name.</p>';
      out.querySelectorAll('[data-open]').forEach((b) => b.addEventListener('click', () => open(b.dataset.open)));
    } catch (err) { out.innerHTML = `<p class="sub">${esc(err.message)}</p>`; }
  };
  const open = async (id) => {
    const box = root.querySelector('#scRecord'); box.innerHTML = '<div class="card"><p class="sub">Loading&hellip;</p></div>';
    try {
      const r = await ctx.api(`/api/scouts/players/${encodeURIComponent(id)}`);
      const rec = r.record; const c = rec.career;
      const t = c?.totals?.tracked; const g = c?.totals?.games;
      box.innerHTML = `<div class="card">
        <h3>${esc(r.player.name)}</h3><p class="sub">${esc(r.why)} Scopes: ${rec.scopes.join(', ')}.</p>
        ${c ? `<p><b>Tracked</b> (measured by the camera): ${t?.attempts ?? 0} shots, ${t?.makes ?? 0} logged makes, average form ${t?.form ?? t?.avgForm ?? '?'}.</p>
        <p><b>Games</b> (logged by the player): ${g?.record ? `${g.record.win}-${g.record.loss}${g.record.tie ? `-${g.record.tie}` : ''} over ${g.record.logged} games` : 'none logged'}.</p>
        <p><b>Teams</b>: ${(c.teams || []).map((x) => `${esc(x.name)}${x.level ? ` (${esc(x.level)})` : ''}${x.season ? ` ${esc(x.season)}` : ''}`).join('; ') || 'none on record'}.</p>
        <p><b>Accolades</b>: ${(c.accolades || []).map((x) => esc(x.title || x.label || x.kind)).join('; ') || 'none on record'}.</p>` : '<p class="sub">No record data.</p>'}
        ${rec.sessions ? `<p><b>Recent sessions</b>: ${rec.sessions.slice(0, 8).map((s) => `${when(s.startedAt)} ${s.attempts} shots, form ${s.avgForm}`).join(' &middot; ') || 'none'}</p>` : ''}
        ${rec.body ? `<p><b>Body</b> (granted): ${rec.body.length} entries.</p>` : ''}
        ${rec.journal ? `<p><b>Journal</b> (granted): ${rec.journal.length} entries.</p>` : ''}
        <p class="sub">A record of what was logged and measured. Not a grade, not a scouting report. Contact with a minor goes through their parent, never to the player.</p>
      </div>`;
    } catch (err) { box.innerHTML = `<div class="card"><p class="sub">${esc(err.message)}</p></div>`; }
  };
  root.querySelector('#scGo').addEventListener('click', search);
  root.querySelector('#scQ').addEventListener('keydown', (e) => { if (e.key === 'Enter') search(); });
}
